import { Domain, Habilitation } from '@core/auth/habilitation/habilitation.model';

/**
 * Une habilitation **grantable** à un profil — miroir de `PermissionResponse` (olive_auth).
 *
 * Forme gelée du contrat `GET /admin/profiles/permissions` :
 * `{ uuid, name, domain, readOnly, crownJewel }`. Le `name` est une `Habilitation` (1 des 70),
 * le `domain` un `Domain` (1 des 15). `readOnly`/`crownJewel` portent les métadonnées de
 * la matrice profils (badge lecture-seule / action sensible réservée ADMIN).
 *
 * ⚠️ Plus de `level`/`module`/`nature`/`wrappable` (modèle legacy 260-permissions supprimé) :
 * le catalogue est désormais le référentiel des habilitations, groupé par `domain`.
 */
export interface Permission {
  uuid: string;
  name: Habilitation;
  domain: Domain;
  readOnly?: boolean;
  crownJewel?: boolean;
}

export class ProfileRestriction {
  id: string = '';
  managedBranches: Set<string> = new Set();
  managedProducts: Set<string> = new Set();

  constructor(restriction?: Partial<ProfileRestriction>) {
    if (restriction) {
      Object.assign(this, restriction);
      if (restriction.managedBranches && Array.isArray(restriction.managedBranches)) {
        this.managedBranches = new Set(restriction.managedBranches as any);
      }
      if (restriction.managedProducts && Array.isArray(restriction.managedProducts)) {
        this.managedProducts = new Set(restriction.managedProducts as any);
      }
    }
  }

  toJSON(): any {
    return {
      id: this.id || undefined,
      managedBranches: Array.from(this.managedBranches),
      managedProducts: Array.from(this.managedProducts)
    };
  }

  static fromJSON(data: any): ProfileRestriction {
    return new ProfileRestriction({
      id: data.id,
      managedBranches: data.managedBranches || [],
      managedProducts: data.managedProducts || []
    });
  }
}

export const VISIBILITY_LEVELS = [
  'SELF',
  'OWN_ENTITY',
  'MANAGED_ENTITIES',
  'OWN_AND_MANAGED',
  'ENTITY_AND_BELOW',
  'COMPANY',
  'ORGANIZATION'
] as const;

export type VisibilityLevel = typeof VISIBILITY_LEVELS[number];

export class Profile {
  id: string = '';
  name: string = '';
  description: string = '';
  permissions: Permission[] = [];
  level: string = '';
  managementEntity: string = '';
  restrictions: ProfileRestriction | null = null;
  /**
   * Visibilité métier (row-level isolation). Pilote le filtre SQL côté services
   * consommateurs via le claim JWT `dataVisibility`. Pas de read/write séparé :
   * l'écriture hérite du périmètre de lecture via `findByUuidInTenant`.
   */
  dataVisibility: VisibilityLevel = 'OWN_ENTITY';

  constructor(profile?: Partial<Profile>) {
    if (profile) {
      Object.assign(this, profile);
      // Normaliser les habilitations (le back fige `uuid` ; tolère `id` legacy transitoire).
      if (profile.permissions) {
        this.permissions = (profile.permissions as any[]).map((p) => ({
          uuid: p.uuid ?? p.id,
          name: p.name,
          domain: p.domain,
          readOnly: p.readOnly,
          crownJewel: p.crownJewel,
        }));
      }
      if (profile.restrictions) {
        this.restrictions = new ProfileRestriction(profile.restrictions);
      }
    }
  }

  /**
   * Converts this profile to a JSON-serializable format for API calls.
   * Le back attend `permissions: Set<UUID>` → on n'envoie que les UUID des habilitations.
   */
  toJSON(): any {
    return {
      id: this.id,
      name: this.name,
      description: this.description,
      permissions: this.permissions.map((p) => p.uuid),
      level: this.level,
      managementEntity: this.managementEntity,
      restrictions: this.restrictions ? this.restrictions.toJSON() : null,
      dataVisibility: this.dataVisibility
    };
  }

  static fromJSON(data: any): Profile {
    return new Profile({
      id: data.id,
      name: data.name,
      description: data.description,
      permissions: data.permissions || [],
      level: data.level,
      managementEntity: data.managementEntity,
      restrictions: data.restrictions ? ProfileRestriction.fromJSON(data.restrictions) : null,
      dataVisibility: data.dataVisibility || 'OWN_ENTITY'
    });
  }
}
